import { ChangeDetectionStrategy, Component, computed, effect, inject, OnDestroy, OnInit, output, signal } from '@angular/core'
import { NgTemplateOutlet } from '@angular/common'
import { CertificateHolderManagerService } from '@features/certificate-holder/services/certificate-holder-manager.service'
import { PermissionService } from '@core/auth/services/permission.service'
import { UnsavedChangesGuard } from '@services/unsaved-changes.guard'
import { CertificateHolderEmail, CertificateHolderForm } from '@features/certificate-holder/models/certificate-holder.model'
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms'
import { ButtonDirective } from 'primeng/button'
import { IftaLabel } from 'primeng/iftalabel'
import { InputText } from 'primeng/inputtext'
import { Textarea } from 'primeng/textarea'
import { Divider } from 'primeng/divider'
import { Tooltip } from 'primeng/tooltip'
import { ViewportService } from '@services/viewport.service'
import { RouterLink } from '@angular/router'
import { AlbertCountrySelectComponent } from '@shared/components/albert-country-select/albert-country-select.component'
import { AlbertSelectComponent } from '@shared/components/albert-select/albert-select.component'
import { PeriodService } from '@features/insured/services/period/period.service'
import { GlobalErrorService } from '@services/global-error.service'
import { toSignal } from '@angular/core/rxjs-interop'
import { startWith } from 'rxjs'

@Component({
  selector: 'albert-ch-display',
  imports: [
    NgTemplateOutlet,
    ReactiveFormsModule,
    ButtonDirective,
    IftaLabel,
    InputText,
    Textarea,
    Divider,
    Tooltip,
    RouterLink,
    AlbertCountrySelectComponent,
    AlbertSelectComponent
  ],
  templateUrl: './certificate-holder-display.component.html',
  styleUrl: './certificate-holder-display.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
})
export class CertificateHolderDisplayComponent implements OnInit, OnDestroy {
  public readonly manager = inject(CertificateHolderManagerService)
  public readonly permissionService = inject(PermissionService)
  public readonly visibilityChange = output()
  private readonly unsavedGuard = inject(UnsavedChangesGuard)
  private readonly viewportService = inject(ViewportService)
  private readonly globalErrorService = inject(GlobalErrorService)
  private readonly periodService = inject(PeriodService)
  public readonly periodOptions = signal<{ label: string; value: string }[]>([])

  /** Client-side filter only, not part of the saved certificate holder form. */
  public readonly linkedCertificatesFilterForm = new FormGroup({
    period: new FormControl<string | null>(null)
  })

  public readonly isMobile = this.viewportService.isMobile

  private readonly usedIn = toSignal(
    this.manager.certificateHolderForm.controls.usedIn.valueChanges.pipe(
      startWith(this.manager.certificateHolderForm.controls.usedIn.value)
    ),
    { initialValue: this.manager.certificateHolderForm.controls.usedIn.value }
  )

  private readonly selectedPeriod = toSignal(
    this.linkedCertificatesFilterForm.controls.period.valueChanges.pipe(
      startWith(this.linkedCertificatesFilterForm.controls.period.value)
    ),
    { initialValue: null }
  )

  public readonly hasLinkedCertificates = computed(() => (this.usedIn() ?? []).length > 0)

  public readonly filteredUsedIn = computed(() => {
    const usedIn = this.usedIn() ?? []
    const selectedPeriod = this.selectedPeriod()
    if (selectedPeriod === null || selectedPeriod === '') return usedIn

    return usedIn.filter(item => item.periodUuid === selectedPeriod)
  })

  constructor() {
    effect(() => {
      if (this.manager.isReadOnly()) {
        this.certificateHolderForm.disable({ emitEvent: false })
      } else {
        this.certificateHolderForm.enable({ emitEvent: false })
      }
    })
  }

  protected get certificateHolderForm(): FormGroup<CertificateHolderForm> {
    return this.manager.certificateHolderForm
  }

  public ngOnInit(): void {
    this.unsavedGuard.register({
      condition: () => this.certificateHolderForm.dirty,
      confirmLeave: () => this.manager.confirmLeave()
    })

    this.periodService.listAll().subscribe({
      next: periods => {
        this.periodOptions.set(periods.map(p => ({ label: p.label, value: p.uuid! })))
        const activePeriod = periods.find(p => p.active)
        if (activePeriod) {
          this.linkedCertificatesFilterForm.controls.period.setValue(activePeriod.uuid ?? null)
        }
      },
      error: () => this.globalErrorService.reportError({
        subsystemName: 'Period Loading',
        description: 'The available periods could not be loaded.'
      })
    })
  }

  public ngOnDestroy(): void {
    this.unsavedGuard.unregister()
  }

  public onEdit(): void {
    this.manager.isReadOnly.set(false)
    this.manager.contentState.set('edit')
  }

  public onDuplication(): void {
    this.manager.duplicate()
  }

  public onClose(): void {
    this.manager.close()
  }

  public onAddEmail(): void {
    const newControl = this.manager.formService.createBlankEmailControl(this.certificateHolderForm.controls.recipientAddresses.getRawValue())
    this.certificateHolderForm.controls.recipientAddresses.push(newControl)

    this.certificateHolderForm.markAsDirty()
  }

  public removeEmail(control: FormControl<CertificateHolderEmail>): void {
    this.certificateHolderForm.controls.recipientAddresses.removeAt(this.certificateHolderForm.controls.recipientAddresses.controls.indexOf(control))
    this.certificateHolderForm.markAsDirty()
  }

  public conditionFormElementValidity(ctrl: AbstractControl | null | undefined): boolean {
    return ((ctrl?.invalid === true) || (this.certificateHolderForm.errors?.['certificateHolderExists'] === true)) && (ctrl?.touched ?? false)
  }

  protected updateEmailValue(control: FormControl<CertificateHolderEmail>, event: Event): void {
    const inputElement = event.target as HTMLInputElement
    control.setValue({
      uuid: control.value.uuid ?? null,
      email: inputElement.value
    })
  }
}
